/**
 * HeaderHost — host wrapper that maps zustand store state to the @gw/ui
 * Header organism and its right-cluster molecules (OrgSwitcher, TokenDisplay,
 * UserMenu).
 *
 * All store coupling lives here; the organism and molecules are pure props.
 */

import { useEffect, useState } from 'react';

import {
  Header,
  OrgSwitcher,
  TokenDisplay,
  UserMenu,
  type OrgSwitcherOrganization,
  type OrgSwitcherStatus,
  type TokenDisplayPackage,
  type UserMenuIdvProp,
  type UserMenuIdvStatus,
  type UserMenuItem,
} from '@gw/ui';
import * as Sentry from '@sentry/react';
import { Clock, Database, Files, ClipboardList, Settings, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { signalsApi } from '@/api/signals';
import { Logo } from '@/components/molecules/Logo';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useHasOrgRole } from '@/hooks/useHasOrgRole';
import { usePricingConfig } from '@/hooks/usePricingConfig';
import { useNotificationsUnreadCount } from '@/lib/notifications/notificationsSlice';
import { queryClient, QUERY_KEYS } from '@/lib/queryClient.util';
import { RouterLink } from '@/router/RouterLink';
import {
  useAuthStore,
  useOrganizationStore,
  useOrganizationTokensStore,
  usePrivyStore,
} from '@/stores/store';
import { GLOBAL_ADMIN_ORG_ID } from '@/types/orgId';
import { extractInitials, getDisplayName } from '@/utils/dataQuality.util';

// GW-430: External help link (opens in new tab)
const HELP_FORM_URL =
  'https://ghostdogbase.atlassian.net/jira/software/form/74d3ed5d-e9b5-4f90-939a-791d58f89aa6';

/**
 * Map the gw-spa OrganizationStatus to OrgSwitcher's OrgSwitcherStatus.
 * @gw/ui OrgSwitcherStatus includes 'pending' and 'inactive' for display
 * purposes; gw-spa only knows 'active', 'suspended', 'archived'.
 */
function toOrgSwitcherStatus(status: string | undefined): OrgSwitcherStatus {
  if (status === 'active') return 'active';
  if (status === 'suspended') return 'suspended';
  // 'archived' maps to 'inactive' for display purposes
  return 'inactive';
}

/**
 * Map earned grants and IDV session state to the UserMenu UserMenuIdvStatus enum.
 *
 * GW-5875 removed the custom:privyLevel JWT claim, so privyLevel is null for
 * all users. The source of truth is the grants API (GET /v1/grants/{userId}).
 */
const TERMINAL_SESSION_STATES = new Set(['verified', 'canceled', 'redacted']);

export function toIdvStatus(
  grants: string[] = [],
  hasActiveSession: boolean,
  sessionStatus?: string | null
): UserMenuIdvStatus {
  if (grants.includes('privy_2')) return 'privy2-granted';
  if (grants.includes('privy_1')) return 'verified';
  if (hasActiveSession && sessionStatus && !TERMINAL_SESSION_STATES.has(sessionStatus))
    return 'in-progress';
  return 'unverified';
}

export interface HeaderHostProps {
  onMenuClick?: () => void;
  className?: string;
}

/**
 * HeaderHost — application header wired to zustand. Swap the four
 * mount-site imports from the local Header to this via the re-export in
 * Header.tsx.
 */
export function HeaderHost({ onMenuClick, className }: HeaderHostProps) {
  const navigate = useNavigate();

  // Auth
  const { user, isAuthenticated, signOut } = useAuthStore();

  // Org
  const { currentOrg, userOrganizations, selectOrganization } = useOrganizationStore();

  // Tokens
  const {
    organizationBalance,
    isLoading: tokensIsLoading,
    fetchOrganizationBalance,
    purchaseOrganizationTokens,
    purchaseCustomTokens,
  } = useOrganizationTokensStore();

  // Config / packages — usePricingConfig auto-fetches on mount so packages
  // are available without the user visiting /tokens first (GW-6718).
  const { config: pricingConfig, isLoading: isLoadingPackages } = usePricingConfig();

  // Privy — GW-5875 removed custom:privyLevel JWT claim; use grants API for IDV status.
  const { idvStatus } = usePrivyStore();

  // IDV status display — modals removed; dropdown navigates to Settings/Privy page

  // GW-6705: earned grants from the signals API (source of truth for IDV status)
  const [grants, setGrants] = useState<string[]>([]);

  useEffect(() => {
    if (!user?.userId) return;
    signalsApi
      .getGrants(user.userId)
      .then((response) => {
        setGrants(response.grants ?? []);
      })
      .catch(() => {
        // Non-fatal: silently fall back to unverified on error
      });
  }, [user?.userId]);

  // Feature flags
  const { isEnabled: queriesEnabled } = useFeatureFlag('datasets_marketplace');
  const { isEnabled: rfisEnabled } = useFeatureFlag('rfi_marketplace');
  // GW-8888 (PRP-PLAT-026 T7, FR-5): header notifications bell. GW-11124:
  // notifications is foundational and must never be feature-flag gated.
  //
  // Narrow selector (NFR-Performance): the bell re-renders only when the
  // unread COUNT changes, never on every notification ingest.
  const unreadCount = useNotificationsUnreadCount();

  // RBAC: can the user purchase tokens?
  const isOrgAdmin = useHasOrgRole('org_admin');

  // Local purchasing state (mirrors TokensPage pattern)
  const [purchasingPackageId, setPurchasingPackageId] = useState<string | null>(null);

  // GW-6704: Fetch balance on mount and whenever the active org changes.
  // Previously balance was only fetched inside handleOrgSwitch (explicit switch),
  // so initial load always showed 0/null.
  useEffect(() => {
    if (currentOrg?.id) fetchOrganizationBalance(currentOrg.id);
  }, [currentOrg?.id, fetchOrganizationBalance]);

  // ─────────────────────────────────────────────────────────────────────────
  // Derived helpers
  // ─────────────────────────────────────────────────────────────────────────

  const firstName = user?.given_name ?? user?.givenName ?? '';
  const lastName = user?.family_name ?? user?.familyName ?? '';
  const displayName = getDisplayName(firstName, lastName, user?.email, user?.username ?? 'User');
  const initials = extractInitials(firstName, lastName, user?.email?.substring(0, 2) ?? 'U');

  // ─────────────────────────────────────────────────────────────────────────
  // OrgSwitcher
  // ─────────────────────────────────────────────────────────────────────────

  // GW-9452 (part of GW-9381): the Global Admin Org sentinel must never be a
  // selectable switch target — exclude it from the OrgSwitcher list.
  const orgSwitcherOrgs: OrgSwitcherOrganization[] = userOrganizations
    .filter((org) => org.id !== GLOBAL_ADMIN_ORG_ID)
    .map((org) => ({
      id: org.id,
      name: org.name,
      status: toOrgSwitcherStatus(org.status),
    }));

  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);

  const handleOrgSwitch = async (orgId: string) => {
    setIsSwitching(true);
    setSwitchingOrgId(orgId);
    try {
      await selectOrganization(orgId);
      fetchOrganizationBalance(orgId).catch(() => {
        // Error is already logged by organizationTokensSlice
      });
      // GW-705: Invalidate org-specific queries after switch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.APPLICATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SESSIONS] });
      navigate('/');
    } catch (error) {
      // GW-9273: surface the failure. Do NOT navigate — the OrgSwitcher stays
      // on the previous org because currentOrg (store) is unchanged.
      toast.error("Couldn't switch organization. Please try again.");
      Sentry.captureException(error, { tags: { feature: 'org-switch' }, extra: { orgId } });
    } finally {
      setIsSwitching(false);
      setSwitchingOrgId(null);
    }
  };

  const orgSwitcherSlot = isAuthenticated ? (
    <OrgSwitcher
      organizations={orgSwitcherOrgs}
      currentOrgId={currentOrg?.id}
      onOrgSwitch={handleOrgSwitch}
      isSwitching={isSwitching}
      switchingOrgId={switchingOrgId}
      testId="org-switcher"
    />
  ) : null;

  // ─────────────────────────────────────────────────────────────────────────
  // TokenDisplay
  // ─────────────────────────────────────────────────────────────────────────

  const balance = organizationBalance?.available ?? null;
  const isLoadingBalance = tokensIsLoading?.['fetchOrganizationBalance'] === true;

  const tokenDisplayPackages: TokenDisplayPackage[] = (pricingConfig?.tokenPackages ?? [])
    .filter((pkg) => pkg.enabled)
    .map((pkg) => ({
      id: pkg.id,
      tokens: pkg.tokens,
      effectivePrice: pkg.effectivePrice,
      basePrice: pkg.basePrice,
      popular: pkg.popular,
    }))
    .sort((a, b) => a.tokens - b.tokens);

  const handleTokenPurchase = async (packageId: string) => {
    if (!currentOrg?.id) return;
    setPurchasingPackageId(packageId);
    try {
      const checkoutUrl = await purchaseOrganizationTokens(currentOrg.id, packageId, 'one_time');
      window.location.href = checkoutUrl;
    } catch {
      setPurchasingPackageId(null);
    }
  };

  const handleCustomTokenPurchase = async (amount: number) => {
    if (!currentOrg?.id) return;
    setPurchasingPackageId('custom');
    try {
      const checkoutUrl = await purchaseCustomTokens(currentOrg.id, amount);
      window.location.href = checkoutUrl;
    } catch {
      setPurchasingPackageId(null);
    }
  };

  const tokenDisplaySlot = isAuthenticated ? (
    <TokenDisplay
      balance={balance}
      isLoading={isLoadingBalance}
      organizationName={currentOrg?.name}
      triggerHref="/tokens"
      canPurchase={isOrgAdmin}
      packages={tokenDisplayPackages}
      isLoadingPackages={isLoadingPackages}
      onPurchase={handleTokenPurchase}
      isPurchasing={purchasingPackageId !== null}
      purchasingPackageId={purchasingPackageId}
      onCustomPurchase={handleCustomTokenPurchase}
      customInvoiceThreshold={50_000}
      isSuspended={currentOrg?.status === 'suspended'}
      manageTokensHref="/tokens"
      transactionsHref="/tokens"
      linkAs={RouterLink}
      testId="token-display"
    />
  ) : null;

  // ─────────────────────────────────────────────────────────────────────────
  // UserMenu
  // ─────────────────────────────────────────────────────────────────────────

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const idvMenuStatus = toIdvStatus(
    grants,
    idvStatus?.hasActiveSession ?? false,
    idvStatus?.sessionStatus
  );

  // GW-6718: upgrade prompt is shown when user has completed privy_1 but not privy_2
  const privy2Available = grants.includes('privy_1') && !grants.includes('privy_2');

  // GW-8263: /settings is the canonical route (flipped from /personal/settings).
  const idvProp: UserMenuIdvProp = {
    status: idvMenuStatus,
    href: '/settings/privy',
    privy2Available,
    testId: `profile-menu-idv-${idvMenuStatus}`,
  };

  const menuItems: UserMenuItem[] = [
    {
      id: 'sessions',
      label: 'My Sessions',
      icon: Clock,
      href: '/personal/sessions',
      testId: 'user-menu-sessions',
    },
    {
      id: 'files',
      label: 'My Files',
      icon: Files,
      href: '/personal/files',
      testId: 'user-menu-files',
    },
    ...(queriesEnabled
      ? [
          {
            id: 'queries',
            label: 'My Queries',
            icon: Database,
            href: '/personal/queries',
            testId: 'user-menu-queries',
          } as UserMenuItem,
        ]
      : []),
    ...(rfisEnabled
      ? [
          {
            id: 'rfis',
            label: 'My RFIs',
            icon: ClipboardList,
            href: '/personal/rfis',
            testId: 'user-menu-rfis',
          } as UserMenuItem,
        ]
      : []),
    { id: 'sep-1', separator: true },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      href: '/settings',
      testId: 'user-menu-settings',
    },
    {
      id: 'help',
      label: 'Help',
      icon: HelpCircle,
      onClick: () => window.open(HELP_FORM_URL, '_blank', 'noopener,noreferrer'),
      testId: 'user-menu-help',
    },
  ];

  const userMenuSlot =
    isAuthenticated && user ? (
      <UserMenu
        displayName={displayName}
        initials={initials}
        organizationName={currentOrg?.name}
        idvStatus={idvProp}
        // GW-8888 (PRP-PLAT-026 T7, FR-5): notifications surface, always
        // rendered (GW-11124: notifications is foundational, never
        // feature-flag gated). Passing notificationCount (a number) is what
        // makes UserMenu render its Bell row + unread badge. This is the
        // spec §6.1 PRIMARY recommendation — the ONE badge surface, so no
        // standalone NotificationBellButton is mounted (a host picks one,
        // not both).
        notificationCount={unreadCount}
        notificationsHref="/personal/notifications"
        items={menuItems}
        onSignOut={handleSignOut}
        linkAs={RouterLink}
        testId="user-menu"
      />
    ) : null;

  // ─────────────────────────────────────────────────────────────────────────
  // Nav links (passed to Header organism)
  // ─────────────────────────────────────────────────────────────────────────

  // GW-9436: the Developers link moved out of the global header into the
  // /publisher shell (PublisherWorkspacePage), next to Support Chat. No
  // top-level nav links remain, so pass undefined (Header renders nothing).
  const navLinks = undefined;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Header
      logo={<Logo />}
      brandHref="/"
      linkAs={RouterLink}
      onMenuClick={onMenuClick}
      user={
        isAuthenticated && user
          ? { displayName, initials, organizationName: currentOrg?.name }
          : undefined
      }
      navLinks={navLinks}
      orgSwitcher={orgSwitcherSlot}
      tokenDisplay={tokenDisplaySlot}
      userMenu={userMenuSlot}
      onSignIn={() => navigate('/auth/login')}
      onSignUp={() => navigate('/auth/signup')}
      className={className}
    />
  );
}
